import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { components } from "@dolfin/api-public";
import { useDolfinClient } from "@/components/DolfinProvider";
import { AccountingSyncBadge } from "@/components/AccountingSync";
import { HeaderRow, TH, TableShell } from "@/components/PageUi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCalendarDate, formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";

type BillResponse = components["schemas"]["BillResponse"];
export type BillPaymentRecord = components["schemas"]["BillPaymentRecordResponse"];
type ManualPaymentMethod = components["schemas"]["ManualPaymentMethod"];

export const BILL_PAYMENT_METHODS: { value: ManualPaymentMethod; label: string }[] = [
  { value: "BankTransfer", label: "Bank transfer" },
  { value: "Cash", label: "Cash" },
  { value: "Cheque", label: "Cheque" },
  { value: "Card", label: "Card" },
  { value: "Other", label: "Other" },
];

// States a manual payment can be recorded in; the backend refuses the rest.
export const BILL_PAYABLE_STATES = new Set(["Approved", "Scheduled", "PaymentFailed", "PartiallyPaid"]);

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function methodLabel(p: BillPaymentRecord) {
  if (p.method === "PayByBank") return "Pay by bank";
  return BILL_PAYMENT_METHODS.find((m) => m.value === p.method)?.label ?? p.method;
}

interface BillPaymentsCardProps {
  bill: BillResponse;
  payments: BillPaymentRecord[];
  loading: boolean;
  canRecord: boolean;
  accountingEnabled: boolean;
  titleClassName?: string;
  nameOf: (userId: string) => string | null | undefined;
  onChanged: () => void;
}

/** A bill's payments: what was paid, when and how, what is left, and recording or removing a manual payment. */
export function BillPaymentsCard({
  bill,
  payments,
  loading,
  canRecord,
  accountingEnabled,
  titleClassName,
  nameOf,
  onChanged,
}: BillPaymentsCardProps) {
  const api = useDolfinClient();
  const currency = bill.currency ?? "GBP";
  const payable = BILL_PAYABLE_STATES.has(bill.state ?? "");

  const [recordOpen, setRecordOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [amount, setAmount] = useState("");
  const [paidOn, setPaidOn] = useState<string | null>(todayIso());
  const [method, setMethod] = useState<ManualPaymentMethod>("BankTransfer");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [removing, setRemoving] = useState<BillPaymentRecord | null>(null);
  const [removingBusy, setRemovingBusy] = useState(false);

  function openRecord(open: boolean) {
    if (open) {
      setAmount(String(bill.outstanding ?? ""));
      setPaidOn(todayIso());
      setMethod("BankTransfer");
      setReference("");
      setNote("");
    }
    setRecordOpen(open);
  }

  async function handleRecord() {
    if (!bill.id || recording) return;
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error("Enter a payment amount greater than zero.");
      return;
    }
    setRecording(true);
    try {
      const { error } = await api.POST("/v1/bills/{id}/payments", {
        params: { path: { id: bill.id } },
        body: {
          amount: value,
          paidOn,
          method,
          reference: reference.trim() || null,
          note: note.trim() || null,
        },
      });
      if (error) {
        toast.error(apiErrorMessage(error, "Failed to record the payment."));
        return;
      }
      toast.success("Payment recorded");
      setRecordOpen(false);
      onChanged();
    } catch {
      toast.error("Failed to record the payment.");
    } finally {
      setRecording(false);
    }
  }

  async function handleRemove() {
    if (!bill.id || !removing?.id || removingBusy) return;
    setRemovingBusy(true);
    try {
      const { error } = await api.DELETE("/v1/bills/{id}/payments/{paymentId}", {
        params: { path: { id: bill.id, paymentId: removing.id } },
      });
      if (error) {
        toast.error(apiErrorMessage(error, "Failed to remove the payment."));
        return;
      }
      toast.success("Payment removed");
      setRemoving(null);
      onChanged();
    } catch {
      toast.error("Failed to remove the payment.");
    } finally {
      setRemovingBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className={titleClassName}>Payments</CardTitle>
        {canRecord && payable && (
          <Button size="sm" variant="outline" onClick={() => openRecord(true)}>
            <Plus className="size-4" />
            Record payment
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Paid</dt>
            <dd className="mt-0.5 font-medium tabular-nums">{formatCurrency(bill.amountPaid ?? 0, currency)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Outstanding</dt>
            <dd
              className={cn(
                "mt-0.5 font-semibold tabular-nums",
                Number(bill.outstanding ?? 0) === 0 && "text-emerald-600 dark:text-emerald-400",
              )}
            >
              {formatCurrency(bill.outstanding ?? 0, currency)}
            </dd>
          </div>
        </dl>

        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Spinner className="size-5 text-muted-foreground" />
          </div>
        ) : payments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No payments recorded yet.</p>
        ) : (
          <TableShell className="rounded-lg bg-transparent backdrop-blur-none">
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Paid</TableHead>
                  <TableHead className={TH}>Reference</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                  <TableHead className="w-8" />
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {payments.map((p) => {
                  const who = p.createdBy ? nameOf(p.createdBy) : null;
                  // Stacked, not one column each: the bill page's side column is too narrow for six columns.
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="align-top whitespace-normal">
                        <div className="text-sm whitespace-nowrap">{formatCalendarDate(p.paidOn ?? "")}</div>
                        <div className="text-xs text-muted-foreground">
                          {methodLabel(p)}
                          {who ? ` · by ${who}` : null}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-40 align-top whitespace-normal">
                        <div className="text-sm break-words">
                          {p.reference ?? <span className="text-muted-foreground">-</span>}
                        </div>
                        {p.note ? <div className="text-xs text-muted-foreground break-words">{p.note}</div> : null}
                      </TableCell>
                      <TableCell className="text-right align-top whitespace-normal">
                        <div className="font-medium tabular-nums whitespace-nowrap">
                          {formatCurrency(p.amount, p.currency ?? currency)}
                        </div>
                        {accountingEnabled ? (
                          <AccountingSyncBadge sync={p.accountingSync} waitsFor="bill" className="whitespace-normal text-right" />
                        ) : null}
                      </TableCell>
                      <TableCell className="align-top">
                        {canRecord && p.provider === "Manual" ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="size-7 text-muted-foreground hover:text-destructive"
                            aria-label="Remove payment"
                            onClick={() => setRemoving(p)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableShell>
        )}
      </CardContent>

      <Dialog open={recordOpen} onOpenChange={openRecord}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record a payment</DialogTitle>
            <DialogDescription>
              Log a payment made outside Dolfin. Pay part of the bill now and the rest later, or all of it at once.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="bill-payment-amount">Amount ({currency})</Label>
                <Input
                  id="bill-payment-amount"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {formatCurrency(bill.outstanding ?? 0, currency)} outstanding
                </p>
              </div>
              <div className="space-y-2">
                <Label>Paid on</Label>
                <DatePicker value={paidOn} onChange={setPaidOn} placeholder="Select a date" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bill-payment-method">Method</Label>
              <Select value={method} onValueChange={(v) => setMethod(v as ManualPaymentMethod)}>
                <SelectTrigger id="bill-payment-method" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BILL_PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bill-payment-reference">Reference</Label>
              <Input
                id="bill-payment-reference"
                maxLength={100}
                placeholder="e.g. cheque number or bank reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bill-payment-note">Note</Label>
              <Textarea
                id="bill-payment-note"
                rows={2}
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordOpen(false)} disabled={recording}>
              Cancel
            </Button>
            <Button onClick={handleRecord} disabled={recording}>
              {recording ? <Spinner className="size-4" /> : null}
              {recording ? "Recording..." : "Record payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this payment?</AlertDialogTitle>
            <AlertDialogDescription>
              {removing
                ? `${formatCurrency(removing.amount, removing.currency ?? currency)} paid on ${formatCalendarDate(removing.paidOn ?? "")} comes off the bill, and the amount is outstanding again. Only remove a payment that was recorded by mistake.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleRemove} disabled={removingBusy}>
              {removingBusy ? <Spinner className="size-4" /> : null}
              Remove payment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
